import { useMemo } from 'react';
import { useFormikContext } from 'formik';

import { ErrorField } from '@/components/error-field';
import { RequiredLabel } from '@/components/required-label';
import { Checkbox } from '@/components/ui/checkbox';
import { Field } from '@/components/ui/field';
import { Input } from '@/components/ui/input';

import { useFetchRawPermissionsQuery } from '@/query/role.query';

import type { CreateRoleFormValues } from '@/schemas/role.schema';

import { useFormikError } from '@/lib/error';

import type { Permission } from '@/types/role.type';

const formatGroupName = (prefix: string) =>
  prefix.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());

const groupPermissions = (permissions: Permission[]) => {
  const groups = new Map<string, Permission[]>();
  for (const p of permissions) {
    const prefix = p.name.split(':')[0];
    if (!groups.has(prefix)) groups.set(prefix, []);
    groups.get(prefix)!.push(p);
  }
  return groups;
};

export const RoleCreateForm = () => {
  const { getFieldProps, setFieldValue, values } = useFormikContext<CreateRoleFormValues>();
  const hasError = useFormikError();

  const { data } = useFetchRawPermissionsQuery();
  const grouped = useMemo(() => groupPermissions(data || []), [data]);

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-2">
        <Field aria-invalid={hasError('name')}>
          <RequiredLabel htmlFor="name" className="mb-1">
            Role name
          </RequiredLabel>
          <Input
            id="name"
            {...getFieldProps('name')}
            placeholder="Enter role name ..."
            aria-invalid={hasError('name')}
            maxLength={20}
          />
          <ErrorField name="name" />
        </Field>
      </div>

      <div className="flex flex-col gap-2">
        <Field aria-invalid={hasError('description')}>
          <RequiredLabel htmlFor="description" className="mb-1">
            Role description
          </RequiredLabel>
          <Input
            id="description"
            {...getFieldProps('description')}
            placeholder="Add description"
            aria-invalid={hasError('description')}
            maxLength={150}
          />
          <ErrorField name="description" />
        </Field>
      </div>

      <div className="flex flex-col gap-2">
        <RequiredLabel className="mb-1">Permissions</RequiredLabel>
        <div className="space-y-4">
          {Array.from(grouped.entries()).map(([group, perms]) => (
            <div key={group} className="px-3 py-2">
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">
                {formatGroupName(group)}
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                {perms.map(p => {
                  const opt = { label: p.label, value: String(p.id) };
                  return (
                    <label
                      key={p.id}
                      htmlFor={`new-role-perm-${p.id}`}
                      className="flex items-center gap-2 cursor-pointer"
                    >
                      <Checkbox
                        id={`new-role-perm-${p.id}`}
                        checked={values.permissions.some(x => x.value === opt.value)}
                        onCheckedChange={v => {
                          const filtered = values.permissions.filter(x => x.value !== opt.value);
                          setFieldValue('permissions', v ? [...filtered, opt] : filtered);
                        }}
                      />
                      <span className="text-sm">{p.label}</span>
                    </label>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
        <ErrorField name="permissions" />
      </div>
    </div>
  );
};
